// ════════════════════════════════════════════════════════════════════════════
//  features/renave/api.ts — RENAVE (estoque de veículos de revenda)
//  Backend: /api/veiculos-estoque (form 270) e /api/renave/config (form 271).
//  Fatia 4: transferências (filial / outra revenda), pendências e conciliação.
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';

// ── Domínios (espelham os enums do backend — serializados como número) ─────

export const SITUACAO = { EmCompra: 1, EmEstoque: 2, Vendido: 3, Transferido: 4, Cancelado: 9 } as const;
export type Situacao = typeof SITUACAO[keyof typeof SITUACAO];

export const SITUACAO_LABEL: Record<number, string> = {
  1: 'Em compra', 2: 'Em estoque', 3: 'Vendido', 4: 'Transferido', 9: 'Cancelado',
};

/** Como o veículo entrou no estoque (OrigemEntradaVeiculo). */
export const ORIGEM = { Compra: 1, TransferenciaFilial: 2, TransferenciaEstabelecimento: 3 } as const;

export const ORIGEM_LABEL: Record<number, string> = {
  1: 'Compra', 2: 'Transferência de filial', 3: 'Transferência de outra revenda',
};

export const TIPO_OPERACAO_LABEL: Record<number, string> = {
  1: 'Entrada em estoque', 2: 'Entrada (veículo próprio)', 3: 'Assinatura do ATPV',
  4: 'NF-e de entrada', 5: 'Saída de estoque', 6: 'NF-e de saída',
  7: 'Cancelamento da entrada', 8: 'Cancelamento da saída', 9: 'Recebimento de filial',
  10: 'Autorização de transferência', 11: 'Cancelamento da autorização', 12: 'Recebimento de outra revenda',
};

export const STATUS_OPERACAO = { EmAndamento: 1, Concluida: 2, Rejeitada: 3, Incerta: 4, Descartada: 5 } as const;

export const STATUS_OPERACAO_LABEL: Record<number, string> = {
  1: 'Em andamento', 2: 'Concluída', 3: 'Recusada', 4: 'Pendente de conferência', 5: 'Não efetivada',
};

export const TIPOS_CRV = ['AZUL', 'VERDE', 'BRANCO', 'DIGITAL'] as const;

/**
 * papelMoeda: o tipo só vale para CRV em papel (AZUL/VERDE/BRANCO); os demais
 * só para CRV DIGITAL (ATPVe). O RENAVE recusa a combinação errada (testado:
 * "não é permitido, nem necessário, enviar assinatura de próprio punho para ATPVe").
 */
export const TIPOS_ASSINATURA = [
  { valor: 'PROPRIO_PUNHO_PAPEL_MOEDA',        rotulo: 'CRV papel-moeda assinado com firma reconhecida (foto)',        arquivo: true,  p7s: false, papelMoeda: true  },
  { valor: 'QUALIFICADA_P7S_FOTO_PAPEL_MOEDA', rotulo: 'Assinatura digital (e-CPF/e-CNPJ) sobre a foto do CRV (.p7s)', arquivo: true,  p7s: true,  papelMoeda: true  },
  { valor: 'PROPRIO_PUNHO_ATPVE',              rotulo: 'ATPVe impresso, assinado com firma reconhecida (foto)',        arquivo: true,  p7s: false, papelMoeda: false },
  { valor: 'QUALIFICADA_P7S_XML_ATPVE',        rotulo: 'Assinatura digital (e-CPF) sobre o XML do ATPVe (.p7s)',        arquivo: false, p7s: true,  papelMoeda: false },
] as const;

export function tiposAssinaturaPara(tipoCrv: string | null) {
  return TIPOS_ASSINATURA.filter(t => t.papelMoeda === (tipoCrv !== 'DIGITAL'));
}

// ── Tipos ───────────────────────────────────────────────────────────────────

export interface VeiculoEstoque {
  id: number;
  empresaId: number;
  produtoId: number;
  situacao: number;
  chassi: string;
  placa: string | null;
  renavam: string | null;
  marca: string | null;
  modelo: string | null;
  anoFabricacao: number | null;
  anoModelo: number | null;
  cor: string | null;
  tipoCrv: string | null;
  numeroCrv: string | null;
  possuiCodigoSegurancaCrv: boolean;
  dataCompra: string | null;
  valorCompra: number | null;
  quilometragemEntrada: number | null;
  dataHoraMedicaoKmEntrada: string | null;
  veiculoProprio: boolean;
  vendedorPessoaId: number | null;
  tipoDocumentoVendedor: string | null;
  documentoVendedor: string | null;
  emailVendedor: string | null;
  dataVenda: string | null;
  valorVenda: number | null;
  compradorPessoaId: number | null;
  tipoDocumentoComprador: string | null;
  documentoComprador: string | null;
  renaveIdEstoque: number | null;
  renaveEstado: string | null;
  renaveSincronizadoEm: string | null;
  chaveNfEntrada: string | null;
  chaveNfSaida: string | null;
  observacao: string | null;
  origemEntrada: number;
  cnpjOrigemTransferencia: string | null;
  veiculoOrigemId: number | null;
  renaveIdAutorizacao: number | null;
  /** CRIADA, CONSUMIDA ou CANCELADA. */
  estadoAutorizacao: string | null;
  cnpjDestinoAutorizacao: string | null;
  valorAutorizacao: number | null;
  /** Achada na última conciliação/atualização com o RENAVE. */
  divergenciaRenave: string | null;
}

export interface VeiculoEstoqueInput {
  chassi: string;
  placa: string | null;
  renavam: string | null;
  marca: string | null;
  modelo: string | null;
  anoFabricacao: number | null;
  anoModelo: number | null;
  cor: string | null;
  tipoCrv: string | null;
  numeroCrv: string | null;
  /** Texto puro; vazio = manter o atual. */
  codigoSegurancaCrv: string | null;
  dataCompra: string | null;
  valorCompra: number | null;
  quilometragemEntrada: number | null;
  dataHoraMedicaoKmEntrada: string | null;
  veiculoProprio: boolean;
  vendedorPessoaId: number | null;
  tipoDocumentoVendedor: string | null;
  documentoVendedor: string | null;
  emailVendedor: string | null;
  observacao: string | null;
  /** 1 = compra (padrão), 3 = receber de outra revenda. Filial só pelo "Receber de filial". */
  origemEntrada?: number;
  cnpjOrigemTransferencia?: string | null;
}

export interface OperacaoRenave {
  id: number;
  tipo: number;
  status: number;
  ambiente: number;
  httpStatus: number | null;
  mensagemUsuario: string | null;
  logIdRastreabilidade: string | null;
  renaveIdEstoque: number | null;
  tentativas: number;
  dtInicio: string;
  dtFim: string | null;
  resolucao: string | null;
}

export interface ResultadoOperacao {
  sucesso: boolean;
  operacaoId: number;
  status: number;
  mensagem: string;
  statusRenave: number | null;
  logIdRastreabilidade: string | null;
}

export interface CrvAtual {
  numeroCrv: string | null;
  pdfCodigoSegurancaBase64: string;
}

export interface VendaInput {
  dataVenda: string;
  valorVenda: number;
  tipoDocumentoComprador: string;
  documentoComprador: string;
  nomeComprador: string;
  emailComprador: string | null;
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string;
  uf: string;
  cidadeIbge: number | null;
  compradorPessoaId: number | null;
  codigoSegurancaCrv: string | null;
  numeroCrv: string | null;
  cpfOperador: string;
}

export interface NotaFiscalVeiculo {
  chave: string;
  numero: number;
  dataEmissao: string;
  documentoDestinatario: string | null;
  nomeDestinatario: string | null;
  logradouro: string | null;
  numeroEndereco: string | null;
  bairro: string | null;
  cep: string | null;
  cidade: string | null;
  uf: string | null;
  cidadeIbge: number;
  valorItem: number;
  cfop: string | null;
  cst: string | null;
  csosn: string | null;
  baseIcms: number;
  aliquotaIcms: number;
  valorIcms: number;
  valorPis: number;
  valorCofins: number;
}

export interface ConferenciaFiscal {
  evento: 'COMPRA' | 'VENDA';
  regime: string;
  nota: NotaFiscalVeiculo | null;
  bloqueios: string[];
  avisos: string[];
  margem: {
    valorCompra: number; valorVenda: number; margem: number;
    aliquotaPis: number; aliquotaCofins: number; pisSobreMargem: number; cofinsSobreMargem: number;
  } | null;
  podeEnviar: boolean;
}

export interface ConfigRenave {
  ativo: boolean;
  ambiente: number;
  emailEstabelecimento: string | null;
  produtoModeloId: number | null;
  produtoModeloDescricao: string | null;
  dtUltimoTeste: string | null;
  ultimoTesteOk: boolean | null;
  cnpjAutenticado: string | null;
  ultimoTesteMensagem: string | null;
  regimeEmpresa: string;
}

export interface TesteConexao {
  ok: boolean;
  mensagem: string;
  cnpjAutenticado: string | null;
  nomeAutenticado: string | null;
  testadoEm: string;
}

export interface DocumentoRenave {
  nomeArquivo: string;
  pdfBase64: string;
  numero: string | null;
}

/** Resposta da aptidão: repassada do RENAVE como veio (AptidaoVeiculoEstoque). */
export interface Aptidao {
  comunicacaoComDetranFalhou?: boolean | null;
  diagnostico?: { veiculoApto?: boolean | null; motivosParaNaoAptidao?: string[] | null } & Record<string, unknown>;
  veiculo?: { chassi?: string | null; placa?: string | null; renavam?: string | null; ufJurisdicao?: string | null } | null;
  informacoesDebitos?: {
    existemDebitos?: boolean | null;
    valorDebitoIpva?: number | null;
    valorDebitoLicenciamento?: number | null;
    valorDebitoMultas?: number | null;
    valorDebitoDpvat?: number | null;
    valorTaxasEDebitosDiversos?: number | null;
    boletos?: { dataVencimentoBoleto?: string | null; numeroCodigoBarras?: string | null; valorBoleto?: number | null }[] | null;
  } | null;
}

export interface AutorizacaoRecebida {
  id: number;
  placa: string | null;
  chassi: string | null;
  cnpjOrigem: string | null;
  nomeOrigem: string | null;
  dataHoraAutorizacao: string | null;
  veiculoCadastradoId: number | null;
}

export interface VeiculoFilial {
  veiculoId: number;
  empresaId: number;
  nomeEmpresa: string | null;
  cnpjEmpresa: string | null;
  chassi: string;
  placa: string | null;
  marca: string | null;
  modelo: string | null;
  anoModelo: number | null;
  valorCompra: number | null;
  quilometragem: number | null;
  renaveEstado: string | null;
  /** null = pode transferir. */
  impedimento: string | null;
}

export interface Pendencia {
  veiculoId: number;
  chassi: string;
  placa: string | null;
  descricao: string | null;
  tipo: string;
  severidade: 'ALTA' | 'MEDIA' | 'INFO';
  mensagem: string;
  desde: string | null;
}

export interface PainelRenave {
  ativo: boolean;
  ultimaConciliacao: string | null;
  resumoConciliacao: string | null;
  pendencias: Pendencia[];
}

export interface Conciliacao {
  verificados: number;
  atualizados: number;
  divergencias: number;
  falhas: number;
  desconhecidosNoGestor: string[];
  resumo: string;
  conciliadoEm: string;
}

export const TIPO_PENDENCIA_LABEL: Record<string, string> = {
  OPERACAO_INCERTA: 'Operação a conferir',
  DIVERGENCIA: 'Divergência com o RENAVE',
  AGUARDANDO_DETRAN: 'Aguardando o Detran',
  NF_ENTRADA_PENDENTE: 'NF-e de entrada',
  ASSINATURA_ATPV_PENDENTE: 'Assinatura do ATPV',
  NF_SAIDA_PENDENTE: 'NF-e de saída',
  COMPRA_SEM_ENTRADA: 'Sem entrada no RENAVE',
  AUTORIZACAO_ABERTA: 'Transferência autorizada',
};

// ── Chamadas ────────────────────────────────────────────────────────────────

const BASE = '/api/veiculos-estoque';

// O apiClient corta em 15 s; o RENAVE pode levar até 60 s (GET) / 120 s (POST)
// segundo o manual ("rajadas") + a ida pelo serviço fiscal. Abortar antes deixaria
// o operador achando que falhou enquanto a operação conclui no servidor.
const TIMEOUT_LEITURA = 75_000;
const TIMEOUT_ESCRITA = 160_000;
const leitura = { timeout: TIMEOUT_LEITURA };

export const renaveApi = {
  listar: (params: { situacao?: number; busca?: string }) =>
    apiClient.get<VeiculoEstoque[]>(BASE, { params }).then(r => r.data),
  obter: (id: number) => apiClient.get<VeiculoEstoque>(`${BASE}/${id}`).then(r => r.data),
  criar: (dados: VeiculoEstoqueInput) => apiClient.post<VeiculoEstoque>(BASE, dados).then(r => r.data),
  atualizar: (id: number, dados: VeiculoEstoqueInput) =>
    apiClient.put<VeiculoEstoque>(`${BASE}/${id}`, dados).then(r => r.data),
  operacoes: (id: number) => apiClient.get<OperacaoRenave[]>(`${BASE}/${id}/operacoes`).then(r => r.data),

  aptidao: (id: number) => apiClient.get<Aptidao>(`${BASE}/${id}/renave/aptidao`, leitura).then(r => r.data),
  sincronizar: (id: number) => apiClient.post<VeiculoEstoque>(`${BASE}/${id}/renave/sincronizar`, undefined, leitura).then(r => r.data),
  atpv: (id: number) => apiClient.get<DocumentoRenave>(`${BASE}/${id}/renave/atpv`, leitura).then(r => r.data),
  termoEntrada: (id: number) => apiClient.get<DocumentoRenave>(`${BASE}/${id}/renave/termo-entrada`, leitura).then(r => r.data),

  // Escritas: 200 concluída, 202 pendente de conferência, 422 recusada.
  entrada: (id: number, cpfOperador: string) =>
    escrita(`${BASE}/${id}/renave/entrada`, { cpfOperador }),
  assinaturaAtpv: (id: number, dados: { tipo: string; arquivoBase64: string | null; assinaturaP7sBase64: string | null; cpfOperador: string }) =>
    escrita(`${BASE}/${id}/renave/assinatura-atpv`, dados),
  notaFiscalEntrada: (id: number, chaveNfe: string, cpfOperador: string) =>
    escrita(`${BASE}/${id}/renave/nota-fiscal-entrada`, { chaveNfe, cpfOperador }),
  cancelarEntrada: (id: number, cpfOperador: string) =>
    escrita(`${BASE}/${id}/renave/cancelar-entrada`, { cpfOperador }),
  conferir: (id: number) => escrita(`${BASE}/${id}/renave/conferir`, {}),

  // Venda (Fatia 3)
  crvAtual: (id: number) => apiClient.get<CrvAtual>(`${BASE}/${id}/renave/crv-atual`, leitura).then(r => r.data),
  crlve: (id: number) => apiClient.get<DocumentoRenave>(`${BASE}/${id}/renave/crlve`, leitura).then(r => r.data),
  saida: (id: number, dados: VendaInput) => escrita(`${BASE}/${id}/renave/saida`, dados),
  notaFiscalSaida: (id: number, chaveNfe: string, cpfOperador: string) =>
    escrita(`${BASE}/${id}/renave/nota-fiscal-saida`, { chaveNfe, cpfOperador }),
  cancelarSaida: (id: number, cpfOperador: string) =>
    escrita(`${BASE}/${id}/renave/cancelar-saida`, { cpfOperador }),
  conferenciaFiscal: (id: number, evento: 'COMPRA' | 'VENDA', chave?: string) =>
    apiClient.get<ConferenciaFiscal>(`${BASE}/${id}/conferencia-fiscal`, { params: { evento, chave: chave || undefined } })
      .then(r => r.data),

  // Transferências, pendências e conciliação (Fatia 4)
  autorizarTransferencia: (id: number, cnpjDestino: string, valor: number, cpfOperador: string) =>
    escrita(`${BASE}/${id}/renave/autorizar-transferencia`, { cnpjDestino, valor, cpfOperador }),
  cancelarAutorizacao: (id: number, cpfOperador: string) =>
    escrita(`${BASE}/${id}/renave/cancelar-autorizacao`, { cpfOperador }),
  autorizacoesRecebidas: () =>
    apiClient.get<AutorizacaoRecebida[]>(`${BASE}/renave/autorizacoes-recebidas`, leitura).then(r => r.data),
  veiculosDeFiliais: () => apiClient.get<VeiculoFilial[]>(`${BASE}/renave/filiais`).then(r => r.data),
  /** 200 concluída · 202 a conferir · 422 recusada — sempre com o id do veículo criado nesta empresa. */
  receberDeFilial: async (origemId: number, dados: {
    quilometragem: number; dataHoraMedicao: string; dataTransferencia: string; valor: number | null; cpfOperador: string;
  }) => {
    const resp = await apiClient.post<{ veiculoId: number; resultado: ResultadoOperacao }>(
      `${BASE}/renave/filiais/${origemId}/receber`, dados,
      { timeout: TIMEOUT_ESCRITA, validateStatus: s => (s >= 200 && s < 300) || s === 422 });
    if (resp.status === 422 && !resp.data?.resultado) {
      throw Object.assign(new Error((resp.data as { mensagem?: string })?.mensagem ?? 'Operação recusada.'), { response: resp });
    }
    return resp.data;
  },
  pendencias: (dias?: number) =>
    apiClient.get<PainelRenave>(`${BASE}/renave/pendencias`, { params: { dias } }).then(r => r.data),
  // Relê cada veículo aberto no RENAVE: pode levar alguns minutos com estoque grande.
  conciliar: () => apiClient.post<Conciliacao>(`${BASE}/renave/conciliar`, undefined, { timeout: 600_000 }).then(r => r.data),

  config: () => apiClient.get<ConfigRenave>('/api/renave/config').then(r => r.data),
  salvarConfig: (dados: { ativo: boolean; ambiente: number; emailEstabelecimento: string | null; produtoModeloId: number | null }) =>
    apiClient.put('/api/renave/config', dados),
  testarConexao: () => apiClient.post<TesteConexao>('/api/renave/config/testar', undefined, leitura).then(r => r.data),
};

/**
 * Escritas devolvem ResultadoOperacao também na recusa (422): normaliza para
 * o chamador sempre receber o resultado; outros erros (400/409) seguem como exceção.
 */
async function escrita(url: string, body: unknown): Promise<ResultadoOperacao> {
  const resp = await apiClient.post<ResultadoOperacao>(url, body, {
    timeout: TIMEOUT_ESCRITA,
    validateStatus: s => (s >= 200 && s < 300) || s === 422,
  });
  if (resp.status === 422 && !(resp.data && 'operacaoId' in resp.data)) {
    throw Object.assign(new Error((resp.data as { mensagem?: string })?.mensagem ?? 'Operação recusada.'), { response: resp });
  }
  return resp.data;
}

// ── Utilitários ─────────────────────────────────────────────────────────────

/** Abre o PDF numa aba nova (para o operador LER, ex.: código de segurança do CRV). */
export function abrirPdf(base64: string) {
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function baixarPdf(doc: DocumentoRenave) {
  const bytes = Uint8Array.from(atob(doc.pdfBase64), c => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = doc.nomeArquivo;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function lerArquivoBase64(arquivo: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error ?? new Error('Falha ao ler o arquivo.'));
    r.readAsDataURL(arquivo);
  });
}

export function cpfValido(valor: string): boolean {
  const cpf = valor.replace(/\D/g, '');
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  const dv = (n: number) => {
    let soma = 0;
    for (let i = 0; i < n; i++) soma += Number(cpf[i]) * (n + 1 - i);
    const r = soma % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return dv(9) === Number(cpf[9]) && dv(10) === Number(cpf[10]);
}

/** CPF do operador lembrado por usuário (o RENAVE exige; cad_usuario não tem CPF). */
export function cpfOperadorSalvo(usuarioId: number | undefined): string {
  try { return localStorage.getItem(`renave.cpfOperador.${usuarioId ?? 0}`) ?? ''; } catch { return ''; }
}

export function salvarCpfOperador(usuarioId: number | undefined, cpf: string) {
  try { localStorage.setItem(`renave.cpfOperador.${usuarioId ?? 0}`, cpf); } catch { /* storage indisponível */ }
}

export function formatarMoeda(v: number | null | undefined) {
  return v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function formatarCnpj(c: string | null | undefined) {
  const d = (c ?? '').replace(/\D/g, '');
  return d.length === 14 ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}` : (c || '—');
}

export function descricaoVeiculo(v: Pick<VeiculoEstoque, 'marca' | 'modelo' | 'anoFabricacao' | 'anoModelo'>) {
  const anos = v.anoFabricacao || v.anoModelo ? `${v.anoFabricacao ?? '?'}/${v.anoModelo ?? '?'}` : '';
  return [v.marca, v.modelo, anos].filter(Boolean).join(' ') || 'Veículo';
}
